import { motion } from 'framer-motion';
import { Lightbulb, Handshake, TrendingUp, Home, Users, Briefcase, Award } from 'lucide-react'; 
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { aboutService } from '../services/aboutService';
import { useError } from '../context/ErrorContext';
import { Helmet } from 'react-helmet-async';

const About = () => {
  const [aboutContent, setAboutContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showError } = useError();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await aboutService.getAboutContent();
        setAboutContent(data);
      } catch (error) {
        showError('Failed to load About page content: ' + error.message, 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [showError]);

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  if (loading) {
    return (
       <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
          </div>
    );
  }

  if (!aboutContent) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <p className="text-lg text-red-600">Failed to load About page content.</p>
      </div>
    );
  }

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'Lightbulb': return <Lightbulb size={28} />;
      case 'Handshake': return <Handshake size={28} />;
      case 'TrendingUp': return <TrendingUp size={28} />;
      case 'Home': return <Home size={28} />;
      case 'Users': return <Users size={28} />;
      case 'Briefcase': return <Briefcase size={28} />;
      case 'Award': return <Award size={28} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
       <Helmet>
        <title>About Us | Antacres</title>
        <meta 
          name="description" 
          content="Learn about Antacres - our mission, vision, core principles, and our expert team dedicated to helping you find your dream property in India." 
        />
      </Helmet>
      {/* Hero Section */}
      <div className="relative h-[60vh] md:h-[70vh] bg-gradient-to-br from-accent
       to-accent-500 overflow-hidden flex items-center justify-center"
           style={{ backgroundImage: aboutContent.hero.image ? `url(${aboutContent.hero.image})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 z-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

        <div className="relative z-10 text-center text-white p-4">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-4xl md:text-6xl font-extrabold mb-4 drop-shadow-lg"
          >
            {aboutContent.hero.heading}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
            className="text-lg md:text-xl max-w-3xl mx-auto opacity-90"
          >
            {aboutContent.hero.subheading}
          </motion.p>
        </div>
      </div>

      {/* Mission & Vision Section */}
      <motion.section
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ amount: 0.3 }}
        className="py-16 md:py-24 px-4 bg-white relative"
      >
        {aboutContent.mission_vision.image && (
          <div className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{ backgroundImage: `url(${aboutContent.mission_vision.image})` }}
          ></div>
        )}
        <div className="relative z-10 container mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div variants={itemVariants}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 pl-4">
              {aboutContent.mission_vision.mission_heading}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {aboutContent.mission_vision.mission_text_1}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {aboutContent.mission_vision.mission_text_2}
            </p>
          </motion.div>
          <motion.div variants={itemVariants}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 pl-4">
              {aboutContent.mission_vision.vision_heading}
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {aboutContent.mission_vision.vision_text}
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Our Principles Section */}
      <motion.section
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ amount: 0.3 }}
        className="py-16 md:py-24 px-4 bg-gray-100"
      >
        <div className="container mx-auto text-center">
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
            Our Core Principles
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {aboutContent.principles.map((principle, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="flex flex-col items-center text-center"
              >
                <div className="text-accent
                 mb-4">
                  {getIconComponent(principle.icon)} 
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{principle.title}</h3>
                <p className="text-gray-600">{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Glance Stats Section */}
      <motion.section
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ amount: 0.3 }}
        className="py-16 md:py-24 bg-accent
         text-white"
      >
        <div className="container mx-auto px-4 text-center">
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold mb-12">
            Antacres at a Glance
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {aboutContent.glance_stats.map((stat, index) => (
              <motion.div key={index} variants={itemVariants} className="p-6">
                <p className="text-5xl font-extrabold mb-2">{stat.number}</p>
                <p className="text-lg opacity-80">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Team Section */}
      <motion.section
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ amount: 0.3 }}
        className="py-16 md:py-24 px-4 bg-white"
      >
        <div className="container mx-auto text-center">
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
            Meet Our Expert Team
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {aboutContent.team.map((member, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="flex flex-col items-center"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full object-cover mb-4 border-4 border-accent
                   shadow-md"
                />
                <h3 className="text-xl font-semibold text-gray-900">{member.name}</h3>
                <p className="text-accent
                 text-sm mt-1">{member.position}</p>
                <p className="text-gray-600 text-center mt-3 text-sm">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Call to Action Section */}
      <section className="bg-accent text-white py-16 md:py-20 text-center">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ }} 
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold mb-6 drop-shadow-md"
          >
            {aboutContent.cta.heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ }} 
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-xl max-w-3xl mx-auto mb-10 opacity-90"
          >
            {aboutContent.cta.subheading}
          </motion.p>
        <Link to={aboutContent.cta.button_link}>
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ }} 
            transition={{ delay: 0.4, duration: 0.6, ease: "backOut" }}
            whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(0,0,0,0.2)" }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-accent
             font-bold py-3 px-8 rounded-full text-lg shadow-lg"
          >
            {aboutContent.cta.button_text}
          </motion.button>
        </Link>
        </div>
      </section>
    </div>
  );
};

export default About;